import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, X } from 'lucide-react';
import { CertificationType } from '@/hooks/useDiversityAnalytics';
import { cn } from '@/lib/utils';

interface CertificationMultiSelectProps {
  certificationTypes: CertificationType[];
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  isLoading?: boolean;
}

export function CertificationMultiSelect({
  certificationTypes,
  selectedIds,
  onChange,
  isLoading = false,
}: CertificationMultiSelectProps) {
  const [open, setOpen] = useState(false);

  const allSelected = selectedIds.length === 0 || selectedIds.length === certificationTypes.length;

  const handleToggle = (id: string) => {
    if (selectedIds.includes(id)) {
      // Remove
      const newIds = selectedIds.filter(i => i !== id);
      onChange(newIds);
    } else {
      // Add
      onChange([...selectedIds, id]);
    }
  };

  const handleSelectAll = () => {
    onChange([]);
  };

  const handleClearAll = () => {
    // Select none (which means filter to nothing - may want to reconsider this UX)
    onChange([]);
  };

  const getSelectedLabels = () => {
    if (allSelected) return 'All Certifications';
    if (selectedIds.length === 0) return 'All Certifications';
    if (selectedIds.length === 1) {
      const cert = certificationTypes.find(c => c.id === selectedIds[0]);
      return cert?.name || 'Unknown';
    }
    return `${selectedIds.length} certifications`;
  };

  // Group by category
  const groupedCerts = certificationTypes.reduce((acc, cert) => {
    const group = cert.categoryName || 'Other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(cert);
    return acc;
  }, {} as Record<string, CertificationType[]>);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between min-w-[200px]"
          disabled={isLoading}
        >
          <span className="truncate">{getSelectedLabels()}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" align="start">
        <div className="p-3 border-b">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Filter by Certification</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-auto p-1 text-xs"
              onClick={handleSelectAll}
            >
              {allSelected ? 'Clear all' : 'Select all'}
            </Button>
          </div>
        </div>
        <div className="max-h-[300px] overflow-y-auto p-2">
          {Object.entries(groupedCerts).map(([category, certs]) => (
            <div key={category} className="mb-3">
              <p className="text-xs font-medium text-muted-foreground px-2 py-1">
                {category}
              </p>
              <div className="space-y-1">
                {certs.map((cert) => (
                  <label
                    key={cert.id}
                    className={cn(
                      'flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer hover:bg-muted transition-colors',
                      selectedIds.includes(cert.id) && 'bg-muted'
                    )}
                  >
                    <Checkbox
                      checked={allSelected || selectedIds.includes(cert.id)}
                      onCheckedChange={() => handleToggle(cert.id)}
                    />
                    <span className="text-sm">{cert.name}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Badge display for selected certifications
export function CertificationBadge({ name, onRemove }: { name: string; onRemove?: () => void }) {
  return (
    <Badge variant="secondary" className="gap-1">
      {name}
      {onRemove && (
        <button onClick={onRemove} className="ml-1 hover:text-destructive">
          <X className="h-3 w-3" />
        </button>
      )}
    </Badge>
  );
}

// Certification badge colors for consistent display
export const CERTIFICATION_COLORS: Record<string, { bg: string; text: string }> = {
  'MBE': { bg: 'bg-blue-100', text: 'text-blue-800' },
  'WBE': { bg: 'bg-pink-100', text: 'text-pink-800' },
  'VBE': { bg: 'bg-green-100', text: 'text-green-800' },
  'LGBTBE': { bg: 'bg-purple-100', text: 'text-purple-800' },
  'DOBE': { bg: 'bg-amber-100', text: 'text-amber-800' },
  'Native American-Owned': { bg: 'bg-red-100', text: 'text-red-800' },
  'Asian-Pacific-Owned': { bg: 'bg-cyan-100', text: 'text-cyan-800' },
  'Black-Owned': { bg: 'bg-slate-100', text: 'text-slate-800' },
  'Hispanic-Owned': { bg: 'bg-orange-100', text: 'text-orange-800' },
  'SBA Certified Small Business': { bg: 'bg-emerald-100', text: 'text-emerald-800' },
  'Small Disadvantaged Business (SDB)': { bg: 'bg-teal-100', text: 'text-teal-800' },
  'HUBZone': { bg: 'bg-indigo-100', text: 'text-indigo-800' },
  '8(a) Program': { bg: 'bg-violet-100', text: 'text-violet-800' },
};

export function CertificationDisplayBadge({ name }: { name: string }) {
  const colors = CERTIFICATION_COLORS[name] || { bg: 'bg-gray-100', text: 'text-gray-800' };
  
  return (
    <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', colors.bg, colors.text)}>
      {name}
    </span>
  );
}
